import type React from 'react';
import { useEffect, useState } from 'react';
import {
	type Edge,
	attachClosestEdge,
	extractClosestEdge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
	draggable,
	dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';

export type TabDragState =
	| { type: 'idle' }
	| { type: 'preview'; container: HTMLElement }
	| { type: 'dragging' };

export function useDraggableTabs({
	isDragEnabled,
	position,
	id,
	handleDrag,
	isSelected,
	elementRef,
	draggableRef,
}: {
	isDragEnabled: boolean;
	position?: number;
	id?: string;
	handleDrag?: () => void;
	isSelected?: boolean;
	elementRef: React.RefObject<HTMLElement>;
	draggableRef: React.RefObject<HTMLElement>;
}) {
	const [closestEdge, setClosestEdge] = useState<Edge | null>(null);
	const [dragState, setDragState] = useState<TabDragState>({ type: 'idle' });

	useEffect(() => {
		const element = elementRef.current;
		const draggableElement = draggableRef.current;

		if (!element || !draggableElement) return;
		return combine(
			draggable({
				element,
				dragHandle: draggableElement,
				onGenerateDragPreview({ nativeSetDragImage }) {
					setCustomNativeDragPreview({
						nativeSetDragImage,
						getOffset: ({ container }) => {
							const rect = container.getBoundingClientRect();
							// 12 is the offset from the left edge of the tab to the cursor
							// it is defined by the sum of the padding left + the half the icon width
							return { x: 12, y: rect.height / 2 };
						},
						render({ container }) {
							setDragState({ type: 'preview', container });

							return () => setDragState({ type: 'dragging' });
						},
					});
				},
				canDrag: () => isDragEnabled,
				onDragStart: handleDrag,
				onDrag: () => {
					setDragState({ type: 'dragging' });
				},
				onDrop: () => {
					setDragState({ type: 'idle' });
				},
				getInitialData() {
					return { position, id, type: 'tab' };
				},
			}),
			dropTargetForElements({
				element,
				canDrop: (args) => args.source.data.type === 'tab',
				getIsSticky: () => true,
				getData: ({ input, element: dataElement }) => {
					const data = {
						id,
						position,
					};
					return attachClosestEdge(data, {
						input,
						element: dataElement,
						allowedEdges: ['left', 'right'],
					});
				},
				onDrag({ source, self }) {
					if (source.data.id !== id) {
						setClosestEdge(extractClosestEdge(self.data));
					}
				},
				onDragLeave: () => {
					setClosestEdge(null);
				},
				onDrop: () => {
					setClosestEdge(null);
				},
			}),
		);
	}, [isDragEnabled, position, id, isSelected, elementRef, draggableRef, handleDrag]);

	return { closestEdge, dragState };
}
